import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Heartbeat, ChatCircleDots, Trophy, ChartLine, Fire, MoonStars,
  Barbell, Target, PaperPlaneTilt, ArrowRight, Sparkle, CheckCircle,
  Warning, Plus, Trash, PencilSimple, Calendar, User, Play, Drop, Scales,
  Flag, MapPin, ListChecks,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar,
  CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Disclaimer } from "@/components/Disclaimer";
import { ConsentPendingBanner } from "@/components/GuardianConsent";
import MessagesThread from "@/components/MessagesThread";
import GuidedTour from "@/components/GuidedTour";
import AthleteQuickStart from "@/components/athlete/AthleteQuickStart";
import DailyCheckIn from "@/components/DailyCheckIn";

const ATHLETE_TOUR_STEPS = [
  { target: "athlete-quickstart", title: "Start here", body: "This checklist walks you through your setup — it ticks itself off as you train, no manual marking." },
  { target: "athlete-tabs", title: "Your command deck", body: "Switch between your readiness, coach chat, PR board and wellness — everything lives in these tabs." },
  { target: "athlete-overview-hero", title: "Readiness at a glance", body: "Your daily readiness score and next meet live up top so you always know where you stand." },
  { target: "athlete-quick-actions", title: "Quick actions", body: "Log a workout, drop a PR, open a program or record recovery — all one tap from here." },
];

const FLAG_STYLES = {
  green: { text: "text-green-500", bg: "bg-green-500/10", border: "border-green-500/40", label: "READY" },
  amber: { text: "text-amber-500", bg: "bg-amber-500/10", border: "border-amber-500/40", label: "CAUTION" },
  red:   { text: "text-red-500",   bg: "bg-red-500/10",   border: "border-red-500/40",   label: "HIGH FATIGUE" },
};

const TABS = [
  { id: "overview",  label: "Overview",  icon: ChartLine },
  { id: "today",     label: "Today",     icon: Play },
  { id: "readiness", label: "Readiness", icon: Heartbeat },
  { id: "coach",     label: "Coach Chat", icon: ChatCircleDots },
  { id: "prs",       label: "PR Board",   icon: Trophy },
  { id: "wellness",  label: "Wellness",   icon: Scales },
  { id: "profile",   label: "Profile",    icon: User },
];

function KpiTile({ icon: Icon, label, value, sub, accent = "text-brand-accent", tint = "", testid }) {
  // When a `tint` (kpi-* class) is given, use the colour-coded tile; else fall
  // back to the neutral tactical-card look.
  if (tint) {
    return (
      <div className={`kpi-tile ${tint}`} data-testid={testid}>
        <div className="flex items-start justify-between mb-2">
          <span className="kpi-pill"><Icon size={18} weight="duotone" /></span>
          <span className="label-tiny">{label}</span>
        </div>
        <div className="kpi-num font-heading text-3xl tabular-nums">{value}</div>
        {sub && <div className="label-tiny text-text-secondary mt-1">{sub}</div>}
      </div>
    );
  }
  return (
    <div className="tactical-card p-4" data-testid={testid}>
      <div className="flex items-start justify-between mb-2">
        <Icon size={18} weight="duotone" className={accent} />
        <span className="label-tiny">{label}</span>
      </div>
      <div className="font-heading text-3xl tabular-nums">{value}</div>
      {sub && <div className="label-tiny text-text-secondary mt-1">{sub}</div>}
    </div>
  );
}

function ReadinessRing({ score, flag }) {
  const style = FLAG_STYLES[flag] || FLAG_STYLES.red;
  const pct = Math.max(0, Math.min(100, score || 0));
  return (
    <div className={`relative w-32 h-32 rounded-full flex items-center justify-center ${style.bg} border-2 ${style.border}`}>
      <div className="text-center">
        <div className={`font-heading text-4xl tabular-nums ${style.text}`}>{pct}</div>
        <div className={`label-tiny ${style.text}`}>{style.label}</div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Meet Countdown — shown on Overview + Today
// ─────────────────────────────────────────────────────────────
function MeetCountdownCard({ meet, onGoProfile }) {
  // Live-ticking countdown recomputed every 60s.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (!meet?.date) return;
    const iv = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(iv);
  }, [meet?.date]);

  if (!meet) {
    return (
      <div className="tactical-card p-5 border-dashed" data-testid="meet-countdown-empty">
        <div className="flex items-center gap-3">
          <Flag size={24} weight="duotone" className="text-text-secondary" />
          <div className="flex-1 min-w-0">
            <div className="label-tiny text-brand-accent mb-0.5">// NEXT MEET</div>
            <div className="text-sm text-text-secondary">
              No meet on the calendar yet. Add one to trigger the peaking countdown.
            </div>
          </div>
          {onGoProfile ? (
            <button
              onClick={onGoProfile}
              className="border border-brand-accent text-brand-accent hover:bg-brand-accent hover:text-surface-base px-3 py-1.5 text-xs label-tiny tracking-widest transition-colors inline-flex items-center gap-1"
              data-testid="meet-countdown-add-btn"
            >
              <Plus size={12} weight="bold" /> ADD MEET
            </button>
          ) : (
            <Link
              to="/athlete?tab=profile"
              className="border border-brand-accent text-brand-accent hover:bg-brand-accent hover:text-surface-base px-3 py-1.5 text-xs label-tiny tracking-widest transition-colors inline-flex items-center gap-1"
              data-testid="meet-countdown-add-btn"
            >
              <Plus size={12} weight="bold" /> ADD MEET
            </Link>
          )}
        </div>
      </div>
    );
  }

  // Recompute days/hours from `now` for live effect.
  let meetDate;
  try { meetDate = new Date(meet.date + "T00:00:00"); } catch { meetDate = null; }
  const msLeft = meetDate ? (meetDate.getTime() - now.getTime()) : 0;
  const totalHours = Math.max(0, Math.floor(msLeft / (1000 * 60 * 60)));
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  const minutes = Math.max(0, Math.floor(msLeft / (1000 * 60)) % 60);
  const isToday = days === 0 && msLeft > 0;
  const isPast  = msLeft <= 0;

  // Peaking bands drive the accent color: <=7d = red urgency, <=21d = amber, else brand-accent.
  const band = isPast ? "past" : days <= 7 ? "peak" : days <= 21 ? "taper" : "build";
  const bandStyle = {
    peak:  { text: "text-red-500",    bg: "bg-red-500/10",    border: "border-red-500/40",    label: "PEAK WEEK" },
    taper: { text: "text-amber-500",  bg: "bg-amber-500/10",  border: "border-amber-500/40",  label: "TAPER PHASE" },
    build: { text: "text-brand-accent", bg: "bg-brand-accent/10", border: "border-brand-accent/40", label: "BUILD PHASE" },
    past:  { text: "text-text-secondary", bg: "bg-white/5", border: "border-white/10", label: "IN PROGRESS" },
  }[band];

  return (
    <div
      className={`tactical-card p-5 relative overflow-hidden border ${bandStyle.border} ${bandStyle.bg}`}
      data-testid="meet-countdown-card"
      data-meet-band={band}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Flag size={14} weight="fill" className={bandStyle.text} />
            <span className={`label-tiny ${bandStyle.text}`}>// NEXT MEET · {bandStyle.label}</span>
            {(band === "peak") && (
              <span className="inline-flex h-2 w-2 rounded-full bg-red-500 animate-pulse" data-testid="meet-countdown-pulse" />
            )}
          </div>
          <div className="font-heading text-2xl truncate" data-testid="meet-countdown-name">{meet.name}</div>
          <div className="text-xs text-text-secondary flex flex-wrap gap-2 mt-1">
            <span>{meet.date}</span>
            {meet.discipline && <span>· {meet.discipline}</span>}
            {meet.location && (
              <span className="inline-flex items-center gap-0.5"><MapPin size={10} /> {meet.location}</span>
            )}
            {meet.goal && <span>· GOAL: <span className="text-white">{meet.goal}</span></span>}
          </div>
        </div>

        <div className="text-right" data-testid="meet-countdown-timer">
          {isPast ? (
            <div className={`font-heading text-2xl ${bandStyle.text}`}>MEET DAY</div>
          ) : isToday ? (
            <div className={`font-heading text-3xl tabular-nums ${bandStyle.text}`} data-testid="meet-countdown-today">
              TODAY
              <div className="label-tiny text-text-secondary mt-0.5">{hours}h {minutes}m</div>
            </div>
          ) : (
            <div className="flex items-baseline gap-2 justify-end">
              <span className={`font-heading text-5xl tabular-nums ${bandStyle.text}`} data-testid="meet-countdown-days">
                {days}
              </span>
              <span className="label-tiny text-text-secondary">
                DAY{days === 1 ? "" : "S"}
                <br />
                LEFT
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Overview
// ─────────────────────────────────────────────────────────────
function CoachSessionsCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // assignment_id
  const [completing, setCompleting] = useState({});

  const load = () => {
    setLoading(true);
    api.get("/athlete/hub/coach-sessions")
      .then(({ data }) => setRows(data.items || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const complete = async (id) => {
    setCompleting((c) => ({ ...c, [id]: true }));
    try {
      await api.post(`/athlete/hub/coach-sessions/${id}/complete`);
      toast.success("Nice work — logged as completed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark complete");
    } finally {
      setCompleting((c) => ({ ...c, [id]: false }));
    }
  };

  if (loading || rows.length === 0) return null;

  return (
    <div className="tactical-card p-5" data-testid="athlete-coach-sessions">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="label-tiny text-brand-accent mb-1">// COACH ASSIGNED · {rows.length}</div>
          <div className="font-heading text-xl">Sessions from your coach</div>
        </div>
      </div>
      <div className="space-y-2">
        {rows.map((a) => {
          const s = a.session || {};
          const isOpen = expanded === a.assignment_id;
          const phases = s.plan?.phases || [];
          const daysLeft = Math.ceil((new Date(a.scheduled_date) - new Date(new Date().toDateString())) / 86400000);
          return (
            <div key={a.assignment_id} className="border border-slate-200 bg-white" data-testid={`athlete-coach-session-${a.assignment_id}`}>
              <button
                onClick={() => setExpanded(isOpen ? null : a.assignment_id)}
                className="w-full flex items-center justify-between gap-3 p-3 text-left hover:bg-slate-50"
                data-testid={`athlete-coach-session-toggle-${a.assignment_id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-heading text-sm truncate">{s.title || "Coach session"}</div>
                  <div className="text-[10px] tracking-widest text-slate-500 mt-1">
                    {(s.sport || "").replace(/_/g, " ").toUpperCase()} · {s.duration_min} MIN · COACH {(a.coach_name || "").split(" ")[0]?.toUpperCase()}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className={`font-heading tabular-nums ${daysLeft <= 1 ? "text-red-500" : daysLeft <= 3 ? "text-amber-600" : "text-cyan-600"}`}>
                    {daysLeft === 0 ? "TODAY" : daysLeft === 1 ? "TOMORROW" : `${daysLeft}D`}
                  </div>
                  <div className="text-[10px] text-slate-400 tabular-nums">
                    {a.scheduled_date}{a.scheduled_time ? ` ${a.scheduled_time}` : ""}
                  </div>
                </div>
              </button>
              {isOpen && (
                <div className="border-t border-slate-100 p-3 bg-slate-50 space-y-3" data-testid={`athlete-coach-session-detail-${a.assignment_id}`}>
                  {a.notes && (
                    <div className="text-xs bg-amber-50 border border-amber-200 p-2">
                      <span className="font-heading text-amber-800">Coach note:</span> <span className="text-slate-700">{a.notes}</span>
                    </div>
                  )}
                  {phases.map((p, i) => (
                    <div key={i}>
                      <div className="label-tiny text-slate-600 mb-1">{(p.name || "").toUpperCase()} · {p.duration_min} MIN</div>
                      <div className="border border-slate-200 bg-white">
                        {(p.activities || []).map((act, j) => (
                          <div key={j} className="grid grid-cols-12 gap-2 px-2 py-1.5 border-b border-slate-100 last:border-0 text-xs items-center">
                            <div className="col-span-2 tabular-nums text-slate-500 font-heading">{act.duration_min}m</div>
                            <div className="col-span-4 font-heading truncate">{act.drill_name}</div>
                            <div className="col-span-6 text-slate-600">{act.note}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {s.plan?.coaching_reminders?.length > 0 && (
                    <div>
                      <div className="label-tiny text-cyan-700 mb-1">COACHING REMINDERS</div>
                      <ul className="list-disc pl-5 text-xs text-slate-700 space-y-0.5">
                        {s.plan.coaching_reminders.map((r, i) => <li key={i}>{r}</li>)}
                      </ul>
                    </div>
                  )}
                  <div className="flex justify-end pt-1">
                    <button
                      onClick={() => complete(a.assignment_id)}
                      disabled={completing[a.assignment_id]}
                      className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs label-tiny tracking-wider px-3 py-1.5"
                      data-testid={`athlete-coach-session-complete-${a.assignment_id}`}
                    >
                      {completing[a.assignment_id] ? "SAVING…" : "MARK COMPLETE"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Goal deadlines — target-date countdowns on the overview
// ─────────────────────────────────────────────────────────────
function GoalDeadlinesCard() {
  const [goals, setGoals] = useState([]);
  const [now, setNow] = useState(() => new Date());

  const loadGoals = useCallback(() => {
    api.get("/goals")
      .then(({ data }) => setGoals(Array.isArray(data) ? data : []))
      .catch(() => setGoals([]));
  }, []);

  useEffect(() => {
    loadGoals();
    const onChange = () => loadGoals();
    window.addEventListener("goals:changed", onChange);
    const iv = setInterval(() => setNow(new Date()), 60000);
    return () => { window.removeEventListener("goals:changed", onChange); clearInterval(iv); };
  }, [loadGoals]);

  // Only active goals that carry a target date, soonest first.
  const dated = goals
    .filter((g) => g.deadline && (g.status || "active") === "active")
    .map((g) => {
      let d = null;
      try { d = new Date(g.deadline + "T00:00:00"); } catch { d = null; }
      const days = d ? Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)) : null;
      return { ...g, _days: days };
    })
    .sort((a, b) => (a._days ?? 1e9) - (b._days ?? 1e9));

  if (dated.length === 0) return null;

  const bandFor = (days) => {
    if (days == null) return { text: "text-text-secondary", border: "border-white/10", label: "" };
    if (days < 0)   return { text: "text-text-secondary", border: "border-white/10", label: "PAST DUE" };
    if (days <= 7)  return { text: "text-red-500", border: "border-red-500/40", label: "FINAL PUSH" };
    if (days <= 21) return { text: "text-amber-500", border: "border-amber-500/40", label: "CLOSING IN" };
    return { text: "text-brand-accent", border: "border-brand-accent/40", label: "ON TRACK" };
  };

  return (
    <div className="tactical-card p-5" data-testid="goal-deadlines-card">
      <div className="flex items-center gap-2 mb-3">
        <Target size={14} weight="fill" className="text-brand-accent" />
        <span className="label-tiny text-brand-accent">// GOAL COUNTDOWN</span>
      </div>
      <div className="space-y-2">
        {dated.map((g) => {
          const b = bandFor(g._days);
          const isPast = g._days != null && g._days < 0;
          const isToday = g._days === 0;
          return (
            <div
              key={g.goal_id}
              className={`flex items-center justify-between gap-3 border ${b.border} p-3`}
              data-testid={`goal-deadline-${g.goal_id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-heading text-sm truncate">{g.title}</div>
                <div className="text-[11px] text-text-secondary flex flex-wrap gap-2 mt-0.5">
                  <span className="inline-flex items-center gap-1"><Calendar size={10} /> {g.deadline}</span>
                  {b.label && <span className={b.text}>· {b.label}</span>}
                </div>
              </div>
              <div className="text-right shrink-0">
                {isPast ? (
                  <span className={`font-heading text-lg ${b.text}`}>DUE</span>
                ) : isToday ? (
                  <span className={`font-heading text-lg ${b.text}`}>TODAY</span>
                ) : (
                  <div className="flex items-baseline gap-1 justify-end">
                    <span className={`font-heading text-2xl tabular-nums ${b.text}`}>{g._days}</span>
                    <span className="label-tiny text-[9px] text-text-secondary">DAY{g._days === 1 ? "" : "S"}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OverviewTab({ overview, onGoTab }) {
  const flag = overview?.readiness?.flag || "red";
  const score = overview?.readiness?.score || 0;
  const style = FLAG_STYLES[flag] || FLAG_STYLES.red;
  const coach = overview?.coaches?.[0];

  return (
    <div className="space-y-6">
      {/* Meet countdown — peaking urgency */}
      <MeetCountdownCard meet={overview?.next_meet} onGoProfile={() => onGoTab("profile")} />

      {/* Goal target-date countdowns */}
      <GoalDeadlinesCard />

      {/* Coach-assigned sessions — shows only when the athlete has any */}
      <CoachSessionsCard />

      {/* Readiness hero */}
      <div className="tactical-card p-6 grid md:grid-cols-3 gap-6 items-center" data-testid="athlete-overview-hero">
        <div className="flex items-center gap-6">
          <ReadinessRing score={score} flag={flag} />
          <div>
            <div className="label-tiny text-brand-accent mb-1">// TODAY&apos;S READINESS</div>
            <div className="font-heading text-2xl">
              {overview?.readiness?.checked_in_today ? "YOU'RE LOGGED IN" : "CHECK IN TO SHARPEN THE PLAN"}
            </div>
            <div className="text-xs text-text-secondary mt-1">
              {overview?.readiness?.checked_in_today
                ? "Coach sees your readiness signal on their dashboard."
                : "A 20-second check-in tunes your program and alerts your coach."}
            </div>
            <button
              onClick={() => onGoTab("readiness")}
              className={`mt-3 btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest inline-flex items-center gap-1.5`}
              data-testid="hero-cta-checkin"
            >
              <Heartbeat size={12} weight="bold" />
              {overview?.readiness?.checked_in_today ? "UPDATE CHECK-IN" : "CHECK IN NOW"}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <KpiTile icon={Fire} label="STREAK" value={overview?.streak_days ?? 0} sub="training days" tint="kpi-orange" testid="kpi-streak" />
          <KpiTile icon={Barbell} label="WORKOUTS 7D" value={overview?.workouts_7d ?? 0} sub="logged" tint="kpi-blue" testid="kpi-workouts-7d" />
          <KpiTile icon={Trophy} label="NEW PRS 30D" value={overview?.prs_30d ?? 0} sub={`${overview?.prs_total ?? 0} all-time`} tint="kpi-amber" testid="kpi-prs-30d" />
          <KpiTile icon={ChatCircleDots} label="UNREAD" value={overview?.unread_messages ?? 0} sub="from coach" tint="kpi-violet" testid="kpi-unread-msgs" />
        </div>
      </div>

      {/* Coach card + quick actions */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="tactical-card p-6" data-testid="athlete-coach-card">
          <div className="label-tiny text-brand-accent mb-2">// YOUR COACH</div>
          {coach ? (
            <div className="flex items-center gap-3">
              {coach.picture ? (
                <img src={coach.picture} alt="" className="w-14 h-14 rounded-full object-cover" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-brand-primary flex items-center justify-center text-lg font-bold">
                  {coach.name?.[0]?.toUpperCase() || "?"}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-heading text-lg truncate">{coach.name}</div>
                <div className="text-xs text-text-secondary">{coach.coach_specialty || "Coach"}</div>
              </div>
              <button
                onClick={() => onGoTab("coach")}
                className="border border-brand-accent text-brand-accent hover:bg-brand-accent hover:text-surface-base px-3 py-1.5 text-xs label-tiny tracking-widest transition-colors inline-flex items-center gap-1"
                data-testid="hero-cta-coach-chat"
              >
                <ChatCircleDots size={12} weight="bold" /> OPEN CHAT
                {coach.unread > 0 && (
                  <span className="ml-1 bg-red-500 text-white text-[10px] rounded-full px-1.5 py-0.5">{coach.unread}</span>
                )}
              </button>
            </div>
          ) : (
            <div className="text-sm text-text-secondary">
              You&apos;re not linked to a coach yet. <Link to="/discover" className="text-brand-accent underline">Find a coach</Link>.
            </div>
          )}
        </div>

        <div className="tactical-card p-6" data-testid="athlete-quick-actions">
          <div className="label-tiny text-brand-accent mb-3">// QUICK ACTIONS</div>
          <div className="grid grid-cols-2 gap-2">
            <Link to="/workouts" className="border border-blue-200 bg-blue-50/40 text-blue-700 hover:bg-blue-100 hover:border-blue-400 px-3 py-3 text-xs label-tiny tracking-widest inline-flex items-center gap-1.5 transition-colors rounded-md" data-testid="qa-log-workout">
              <Barbell size={12} weight="bold" /> LOG WORKOUT
            </Link>
            <button onClick={() => onGoTab("prs")} className="border border-amber-200 bg-amber-50/40 text-amber-700 hover:bg-amber-100 hover:border-amber-400 px-3 py-3 text-xs label-tiny tracking-widest inline-flex items-center gap-1.5 transition-colors rounded-md" data-testid="qa-log-pr">
              <Trophy size={12} weight="bold" /> LOG PR
            </button>
            <Link to="/programs" className="border border-cyan-200 bg-cyan-50/40 text-cyan-700 hover:bg-cyan-100 hover:border-cyan-400 px-3 py-3 text-xs label-tiny tracking-widest inline-flex items-center gap-1.5 transition-colors rounded-md" data-testid="qa-programs">
              <Target size={12} weight="bold" /> PROGRAMS
            </Link>
            <Link to="/recovery" className="border border-emerald-200 bg-emerald-50/40 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-400 px-3 py-3 text-xs label-tiny tracking-widest inline-flex items-center gap-1.5 transition-colors rounded-md" data-testid="qa-recovery">
              <MoonStars size={12} weight="bold" /> RECOVERY LOG
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Readiness — daily check-in + 14-day trend
// ─────────────────────────────────────────────────────────────
function ReadinessTab({ onCheckedIn }) {
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    sleep_hours: 7, sleep_quality: 7, soreness: 4, stress: 4, energy: 7, mood: 7, hrv: "", notes: "",
    pain_severity: "", pain_location: "", pain_notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/athlete/readiness/trend?days=14");
      setTrend(data);
    } catch (e) { /* handled by empty state */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        sleep_hours: parseFloat(form.sleep_hours) || 0,
        sleep_quality: parseInt(form.sleep_quality) || 5,
        soreness: parseInt(form.soreness) || 5,
        stress: parseInt(form.stress) || 5,
        energy: parseInt(form.energy) || 5,
        mood: parseInt(form.mood) || 5,
        hrv: form.hrv === "" ? null : parseFloat(form.hrv),
        notes: form.notes || null,
        pain_severity: form.pain_severity === "" ? null : parseInt(form.pain_severity),
        pain_location: form.pain_location || null,
        pain_notes: form.pain_notes || null,
      };
      const { data } = await api.post("/athlete/readiness/checkin", payload);
      toast.success(`Readiness logged: ${data.readiness_score} / 100 (${data.readiness_flag.toUpperCase()})`);
      await load();
      onCheckedIn?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Check-in failed");
    } finally { setSubmitting(false); }
  };

  const latestFlag = trend?.latest?.flag || "red";
  const latestStyle = FLAG_STYLES[latestFlag] || FLAG_STYLES.red;

  return (
    <div className="space-y-6">
      {/* Trend chart */}
      <div className="tactical-card p-6" data-testid="readiness-trend-card">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
          <div>
            <div className="label-tiny text-brand-accent mb-1">// 14-DAY TREND</div>
            <div className="font-heading text-2xl">READINESS SIGNAL</div>
            <div className="text-xs text-text-secondary">
              Green ≥ 75 · Amber 50-74 · Red &lt; 50
            </div>
          </div>
          {trend?.latest && (
            <div className={`px-3 py-2 border ${latestStyle.border} ${latestStyle.bg} ${latestStyle.text}`}>
              <div className="label-tiny">LATEST · {trend.latest.date}</div>
              <div className="font-heading text-2xl tabular-nums">{trend.latest.score} · {latestStyle.label}</div>
              <div className="label-tiny">AVG {trend.average}</div>
            </div>
          )}
        </div>
        {loading ? (
          <div className="label-tiny text-text-secondary">LOADING...</div>
        ) : (trend?.trend || []).length === 0 ? (
          <div className="text-center py-10 text-text-secondary text-sm" data-testid="readiness-empty">
            No check-ins yet. Log your first one below to start the trend.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={trend.trend}>
              <defs>
                <linearGradient id="rGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#4169E1" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#4169E1" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" stroke="#A1A5B5" fontSize={11} />
              <YAxis domain={[0, 100]} stroke="#A1A5B5" fontSize={11} />
              <Tooltip contentStyle={{ background: "#12141A", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }} />
              <ReferenceLine y={75} stroke="#10b981" strokeDasharray="4 4" />
              <ReferenceLine y={50} stroke="#f59e0b" strokeDasharray="4 4" />
              <Area type="monotone" dataKey="score" stroke="#4169E1" fill="url(#rGrad)" strokeWidth={2} dot={{ r: 3, fill: "#FFEA00" }} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Check-in form */}
      <div className="tactical-card p-6" data-testid="readiness-checkin-form">
        <div className="label-tiny text-brand-accent mb-1">// DAILY CHECK-IN</div>
        <div className="font-heading text-2xl mb-4">
          {trend?.checked_in_today ? "UPDATE TODAY'S SIGNAL" : "HOW ARE YOU FEELING TODAY?"}
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {[
            { key: "sleep_hours", label: "SLEEP (hours)", min: 0, max: 12, step: 0.5, testid: "field-sleep" },
            { key: "sleep_quality", label: "SLEEP QUALITY (1-10)", min: 1, max: 10, step: 1, testid: "field-sleep-quality" },
            { key: "energy", label: "ENERGY (1-10)", min: 1, max: 10, step: 1, testid: "field-energy" },
            { key: "mood", label: "MOOD (1-10)", min: 1, max: 10, step: 1, testid: "field-mood" },
            { key: "soreness", label: "SORENESS (1-10, higher = worse)", min: 1, max: 10, step: 1, testid: "field-soreness" },
            { key: "stress", label: "STRESS (1-10, higher = worse)", min: 1, max: 10, step: 1, testid: "field-stress" },
          ].map((f) => (
            <div key={f.key}>
              <div className="flex items-center justify-between mb-1">
                <label className="label-tiny">{f.label}</label>
                <span className="label-tiny tabular-nums text-brand-accent">{form[f.key]}</span>
              </div>
              <input
                type="range"
                min={f.min}
                max={f.max}
                step={f.step}
                value={form[f.key]}
                onChange={(e) => setField(f.key, e.target.value)}
                className="w-full accent-brand-accent"
                data-testid={f.testid}
              />
            </div>
          ))}
          <div>
            <label className="label-tiny block mb-1">HRV (optional, ms)</label>
            <input
              type="number"
              value={form.hrv}
              onChange={(e) => setField("hrv", e.target.value)}
              placeholder="e.g. 62"
              className="w-full bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
              data-testid="field-hrv"
            />
          </div>
          <div>
            <label className="label-tiny block mb-1">NOTES (optional)</label>
            <input
              type="text"
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
              placeholder="e.g. Slept great, no soreness"
              className="w-full bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
              data-testid="field-notes"
            />
          </div>
        </div>

        {/* Pain — optional. Leave blank if none; blank stays "Not submitted". */}
        <div className="mt-4 border-t border-white/5 pt-4">
          <div className="label-tiny text-brand-accent mb-2">// PAIN (optional — leave blank if none)</div>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="label-tiny block mb-1">SEVERITY (0-10)</label>
              <input
                type="number" min="0" max="10" step="1"
                value={form.pain_severity}
                onChange={(e) => setField("pain_severity", e.target.value)}
                placeholder="—"
                className="w-full bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
                data-testid="field-pain-severity"
              />
            </div>
            <div>
              <label className="label-tiny block mb-1">LOCATION</label>
              <input
                type="text"
                value={form.pain_location}
                onChange={(e) => setField("pain_location", e.target.value)}
                placeholder="e.g. left knee"
                className="w-full bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
                data-testid="field-pain-location"
              />
            </div>
            <div>
              <label className="label-tiny block mb-1">PAIN NOTES</label>
              <input
                type="text"
                value={form.pain_notes}
                onChange={(e) => setField("pain_notes", e.target.value)}
                placeholder="e.g. sharp on descent"
                className="w-full bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
                data-testid="field-pain-notes"
              />
            </div>
          </div>
          <Disclaimer variant="medical" className="mt-3" testid="readiness-medical-disclaimer" />
        </div>
        <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
          <div className="text-xs text-text-secondary">
            Your check-in feeds your readiness score and shows on your coach&apos;s autoregulation panel.
          </div>
          <button
            onClick={submit}
            disabled={submitting}
            className="btn-kinetic px-5 py-2.5 text-xs label-tiny tracking-widest inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="btn-submit-checkin"
          >
            <CheckCircle size={12} weight="bold" /> {submitting ? "SAVING..." : "SUBMIT CHECK-IN"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Coach Chat — thread + quick replies
// ─────────────────────────────────────────────────────────────
const QUICK_REPLIES = [
  "Feeling great — ready to push today.",
  "Need a deload — I'm cooked.",
  "Quick question about form on today's main lift.",
  "Can we swap tomorrow's session? Schedule conflict.",
  "PR alert — hit a new best today!",
];

function CoachChatTab({ overview, onNudge }) {
  const { user } = useAuth();
  const [coach, setCoach] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setCoach(overview?.coaches?.[0] || null);
  }, [overview]);

  const sendQuick = async (text) => {
    if (!coach) return;
    setSending(true);
    try {
      await api.post(`/messages/threads/${coach.link_id}`, { text });
      toast.success("Sent");
      onNudge?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally { setSending(false); }
  };

  if (!coach) {
    return (
      <div className="tactical-card p-10 text-center" data-testid="coach-chat-empty">
        <ChatCircleDots size={40} weight="duotone" className="text-brand-accent mx-auto mb-3" />
        <div className="font-heading text-xl mb-1">NO ACTIVE COACH YET</div>
        <p className="text-sm text-text-secondary mb-4">
          Once a coach adds you, your direct thread lives here.
        </p>
        <Link to="/discover" className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest inline-flex items-center gap-1.5">
          <Sparkle size={12} weight="bold" /> DISCOVER COACHES
        </Link>
      </div>
    );
  }

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="coach-chat-tab">
      <div className="lg:col-span-2">
        <div className="tactical-card p-4">
          <div className="flex items-center gap-3 pb-3 border-b border-white/10 mb-3">
            {coach.picture ? (
              <img src={coach.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-brand-primary flex items-center justify-center text-sm font-bold">
                {coach.name?.[0]?.toUpperCase() || "?"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-heading text-lg truncate">{coach.name}</div>
              <div className="label-tiny text-text-secondary">{coach.coach_specialty || "Coach"}</div>
            </div>
          </div>
          <MessagesThread linkId={coach.link_id} currentUserId={user?.user_id} height="480px" enableTestChips />
        </div>
      </div>

      <div className="space-y-4">
        <div className="tactical-card p-4" data-testid="quick-replies-card">
          <div className="label-tiny text-brand-accent mb-2">// QUICK REPLIES</div>
          <div className="space-y-2">
            {QUICK_REPLIES.map((q) => (
              <button
                key={q}
                onClick={() => sendQuick(q)}
                disabled={sending}
                className="w-full text-left border border-white/10 hover:border-brand-accent hover:text-brand-accent px-3 py-2 text-xs transition-colors disabled:opacity-50"
                data-testid={`quick-reply-${q.slice(0, 12).replace(/\s+/g, "-").toLowerCase()}`}
              >
                <PaperPlaneTilt size={11} weight="bold" className="inline mr-1.5" /> {q}
              </button>
            ))}
          </div>
        </div>

        <div className="tactical-card p-4">
          <div className="label-tiny text-brand-accent mb-2">// TIPS</div>
          <ul className="text-xs text-text-secondary space-y-1.5 list-disc list-inside">
            <li>Send a check-in note before hard sessions.</li>
            <li>Flag pain or unusual soreness early.</li>
            <li>Attach form-check video via Form Check tab.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PR Board — grouped, with target gaps
// ─────────────────────────────────────────────────────────────
function CardioPaceChart({ movement }) {
  const rows = (movement.rows || []).map((r) => ({
    ...r,
    label: r.date ? new Date(r.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "",
  }));
  const fmtPaceClock = (v) => {
    const s = Math.round(v);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  };
  const first = rows[0]?.value;
  const last = rows[rows.length - 1]?.value;
  const delta = first != null && last != null ? last - first : null; // negative = faster
  const testId = `athlete-cardio-graph-${(movement.name || "").replace(/\s+/g, "-")}`;
  return (
    <div className="border border-white/10 p-3" data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <div className="font-heading text-sm truncate">{movement.name}</div>
        <div className="label-tiny text-[10px] text-text-secondary">/ 100m</div>
      </div>
      <div className="flex items-baseline gap-2 mb-2">
        <span className="font-heading text-lg tabular-nums text-brand-accent">{last != null ? fmtPaceClock(last) : "—"}</span>
        {delta != null && delta !== 0 && (
          <span className={`label-tiny text-[10px] tabular-nums ${delta < 0 ? "text-green-500" : "text-red-500"}`}>
            {delta < 0 ? "▼ faster" : "▲ slower"} {fmtPaceClock(Math.abs(delta))}
          </span>
        )}
      </div>
      <ResponsiveContainer width="100%" height={120}>
        <LineChart data={rows} margin={{ top: 4, right: 6, bottom: 0, left: -18 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
          <XAxis dataKey="label" tick={{ fontSize: 9, fill: "#8a8f98" }} tickLine={false} axisLine={false} />
          <YAxis reversed tickFormatter={fmtPaceClock} tick={{ fontSize: 9, fill: "#8a8f98" }} tickLine={false} axisLine={false} width={40} domain={["dataMin - 3", "dataMax + 3"]} />
          <Tooltip
            contentStyle={{ background: "#12141a", border: "1px solid rgba(255,255,255,0.1)", fontSize: 11 }}
            formatter={(v) => [`${fmtPaceClock(v)} / 100m`, "Pace"]}
          />
          <Line type="monotone" dataKey="value" stroke="#4169E1" strokeWidth={2} dot={{ r: 3, fill: "#FFEA00" }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function PRBoardTab() {
  const [board, setBoard] = useState(null);
  const [cardio, setCardio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logOpen, setLogOpen] = useState(false);
  const [targetOpen, setTargetOpen] = useState(null); // exercise name
  const [logForm, setLogForm] = useState({ exercise: "", value: "", unit: "kg", note: "" });
  const [targetForm, setTargetForm] = useState({ target_value: "", unit: "kg", target_date: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/athlete/prs/board");
      setBoard(data);
    } catch (e) { /* handled by empty state */ } finally { setLoading(false); }
    api.get("/athlete/cardio-trends").then(({ data }) => setCardio(data.movements || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitLog = async () => {
    if (!logForm.exercise.trim() || !logForm.value) {
      toast.error("Exercise and value required");
      return;
    }
    try {
      await api.post("/prs", {
        exercise: logForm.exercise.trim(),
        value: parseFloat(logForm.value),
        unit: logForm.unit || "kg",
        note: logForm.note || null,
      });
      toast.success("PR logged 🎯");
      setLogOpen(false);
      setLogForm({ exercise: "", value: "", unit: "kg", note: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log PR");
    }
  };

  const submitTarget = async (exercise) => {
    if (!targetForm.target_value || parseFloat(targetForm.target_value) <= 0) {
      toast.error("Target must be > 0");
      return;
    }
    try {
      await api.post("/athlete/prs/target", {
        exercise,
        target_value: parseFloat(targetForm.target_value),
        unit: targetForm.unit || "kg",
        target_date: targetForm.target_date || null,
      });
      toast.success("Target locked");
      setTargetOpen(null);
      setTargetForm({ target_value: "", unit: "kg", target_date: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const clearTarget = async (exercise) => {
    try {
      await api.delete(`/athlete/prs/target/${encodeURIComponent(exercise)}`);
      toast.success("Target cleared");
      await load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  if (loading) return <div className="label-tiny text-text-secondary">LOADING...</div>;

  const lifts = board?.lifts || [];

  return (
    <div className="space-y-4" data-testid="pr-board-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label-tiny text-brand-accent mb-1">// {board?.total_prs || 0} LIFETIME PRS</div>
          <div className="font-heading text-2xl">CHASE THE NEXT REP.</div>
        </div>
        <button
          onClick={() => setLogOpen(true)}
          className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest inline-flex items-center gap-1.5"
          data-testid="btn-log-pr"
        >
          <Plus size={12} weight="bold" /> LOG PR
        </button>
      </div>

      {cardio.length > 0 && (
        <div className="tactical-card p-5" data-testid="cardio-pace-card">
          <div className="label-tiny text-brand-accent mb-1">// CARDIO PACE · LOWER IS FASTER</div>
          <div className="text-xs text-text-secondary mb-4">Your pace (per 100m) each session — watch it drop as conditioning improves.</div>
          <div className="grid md:grid-cols-2 gap-4">
            {cardio.map((m) => (
              <CardioPaceChart key={m.name} movement={m} />
            ))}
          </div>
        </div>
      )}

      {/* Log PR modal */}
      {logOpen && (
        <div className="tactical-card p-5" data-testid="log-pr-form">
          <div className="label-tiny text-brand-accent mb-3">// NEW PR</div>
          <div className="grid md:grid-cols-4 gap-3">
            <input
              placeholder="Exercise (e.g. Back Squat)"
              value={logForm.exercise}
              onChange={(e) => setLogForm({ ...logForm, exercise: e.target.value })}
              className="md:col-span-2 bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
              data-testid="log-pr-exercise"
            />
            <input
              type="number"
              placeholder="Value"
              value={logForm.value}
              onChange={(e) => setLogForm({ ...logForm, value: e.target.value })}
              className="bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
              data-testid="log-pr-value"
            />
            <select
              value={logForm.unit}
              onChange={(e) => setLogForm({ ...logForm, unit: e.target.value })}
              className="bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
              data-testid="log-pr-unit"
            >
              <option value="kg">kg</option>
              <option value="lbs">lbs</option>
              <option value="reps">reps</option>
              <option value="sec">sec</option>
              <option value="m">m</option>
            </select>
            <input
              placeholder="Note (optional)"
              value={logForm.note}
              onChange={(e) => setLogForm({ ...logForm, note: e.target.value })}
              className="md:col-span-4 bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
              data-testid="log-pr-note"
            />
          </div>
          <div className="mt-4 flex gap-2 justify-end">
            <button onClick={() => setLogOpen(false)} className="border border-white/10 px-4 py-2 text-xs label-tiny tracking-widest hover:border-white/40 transition-colors" data-testid="log-pr-cancel">
              CANCEL
            </button>
            <button onClick={submitLog} className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest" data-testid="log-pr-submit">
              SAVE PR
            </button>
          </div>
        </div>
      )}

      {lifts.length === 0 ? (
        <div className="tactical-card p-10 text-center" data-testid="pr-board-empty">
          <Trophy size={40} weight="duotone" className="text-yellow-500 mx-auto mb-3" />
          <div className="font-heading text-xl mb-1">NO PRS YET</div>
          <p className="text-sm text-text-secondary mb-4">
            Log your first PR to open the board and set your next target.
          </p>
          <button onClick={() => setLogOpen(true)} className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest inline-flex items-center gap-1.5">
            <Plus size={12} weight="bold" /> LOG YOUR FIRST PR
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {lifts.map((lift) => {
            const cur = lift.current_pr;
            const t = lift.target;
            const isEditingTarget = targetOpen === lift.exercise;
            const gap = lift.gap;
            const gapText = gap == null ? null : (gap > 0 ? `+${gap} ${t?.unit || cur?.unit} to go` : gap === 0 ? "TARGET HIT" : `+${Math.abs(gap)} ${cur?.unit} PAST TARGET`);
            const gapColor = gap == null ? "text-text-secondary" : gap > 0 ? "text-amber-500" : "text-green-500";
            return (
              <div key={lift.exercise} className="tactical-card p-4" data-testid={`pr-lift-${lift.exercise.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-heading text-lg truncate">{lift.exercise}</div>
                    <div className="label-tiny text-text-secondary">{lift.attempts} PR{lift.attempts === 1 ? "" : "s"} logged</div>
                  </div>
                  <Trophy size={18} weight="duotone" className="text-yellow-500 shrink-0" />
                </div>
                {cur ? (
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="font-heading text-3xl tabular-nums text-brand-accent">{cur.value}</span>
                    <span className="label-tiny">{cur.unit}</span>
                    <span className="label-tiny text-text-secondary ml-auto">{(cur.date || "").slice(0, 10)}</span>
                  </div>
                ) : (
                  <div className="text-xs text-text-secondary mb-3">No PR yet — target set.</div>
                )}

                {t && !isEditingTarget && (
                  <div className="flex items-center justify-between border-t border-white/5 pt-3 mb-2">
                    <div>
                      <div className="label-tiny text-text-secondary">TARGET</div>
                      <div className="font-heading text-lg tabular-nums">
                        {t.target_value} {t.unit}
                      </div>
                      {t.target_date && (
                        <div className="label-tiny text-text-secondary inline-flex items-center gap-1 mt-0.5">
                          <Calendar size={10} /> {t.target_date}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className={`label-tiny ${gapColor}`}>{gapText}</div>
                      <div className="flex gap-1 mt-1">
                        <button
                          onClick={() => { setTargetOpen(lift.exercise); setTargetForm({ target_value: t.target_value, unit: t.unit, target_date: t.target_date || "" }); }}
                          className="text-text-secondary hover:text-brand-accent text-[10px] label-tiny tracking-wider"
                          data-testid={`target-edit-${lift.exercise.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          <PencilSimple size={12} className="inline" /> EDIT
                        </button>
                        <button
                          onClick={() => clearTarget(lift.exercise)}
                          className="text-text-secondary hover:text-red-500 text-[10px] label-tiny tracking-wider"
                          data-testid={`target-clear-${lift.exercise.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          <Trash size={12} className="inline" /> CLEAR
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {!t && !isEditingTarget && (
                  <button
                    onClick={() => { setTargetOpen(lift.exercise); setTargetForm({ target_value: "", unit: cur?.unit || "kg", target_date: "" }); }}
                    className="w-full border border-dashed border-white/10 hover:border-brand-accent hover:text-brand-accent px-3 py-2 text-xs label-tiny tracking-widest transition-colors inline-flex items-center justify-center gap-1.5"
                    data-testid={`target-add-${lift.exercise.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <Target size={12} weight="bold" /> SET NEXT TARGET
                  </button>
                )}

                {isEditingTarget && (
                  <div className="border-t border-white/5 pt-3">
                    <div className="grid grid-cols-3 gap-2">
                      <input
                        type="number"
                        placeholder="Target"
                        value={targetForm.target_value}
                        onChange={(e) => setTargetForm({ ...targetForm, target_value: e.target.value })}
                        className="bg-surface-elevated border border-white/10 px-2 py-1.5 text-sm focus:outline-none focus:border-brand-accent"
                        data-testid={`target-input-${lift.exercise.toLowerCase().replace(/\s+/g, "-")}`}
                      />
                      <select
                        value={targetForm.unit}
                        onChange={(e) => setTargetForm({ ...targetForm, unit: e.target.value })}
                        className="bg-surface-elevated border border-white/10 px-2 py-1.5 text-sm focus:outline-none focus:border-brand-accent"
                      >
                        <option value="kg">kg</option>
                        <option value="lbs">lbs</option>
                        <option value="reps">reps</option>
                        <option value="sec">sec</option>
                        <option value="m">m</option>
                      </select>
                      <input
                        type="date"
                        value={targetForm.target_date}
                        onChange={(e) => setTargetForm({ ...targetForm, target_date: e.target.value })}
                        className="bg-surface-elevated border border-white/10 px-2 py-1.5 text-sm focus:outline-none focus:border-brand-accent"
                      />
                    </div>
                    <div className="flex justify-end gap-1 mt-2">
                      <button onClick={() => setTargetOpen(null)} className="text-text-secondary text-[10px] label-tiny px-2 py-1">CANCEL</button>
                      <button
                        onClick={() => submitTarget(lift.exercise)}
                        className="btn-kinetic px-3 py-1 text-[10px] label-tiny tracking-widest"
                        data-testid={`target-save-${lift.exercise.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        SAVE
                      </button>
                    </div>
                  </div>
                )}

                {lift.history.length > 1 && (
                  <div className="mt-3 pt-3 border-t border-white/5">
                    <div className="label-tiny text-text-secondary mb-1">HISTORY</div>
                    <div className="space-y-1 max-h-24 overflow-y-auto no-scrollbar">
                      {lift.history.slice(1, 6).map((h) => (
                        <div key={h.pr_id} className="text-xs flex justify-between">
                          <span className="tabular-nums">{h.value} {h.unit}</span>
                          <span className="text-text-secondary">{(h.date || "").slice(0, 10)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Today — session card + weekly compliance ring
// ─────────────────────────────────────────────────────────────
function ComplianceRing({ pct, size = 100, stroke = 10 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const val = Math.max(0, Math.min(100, pct ?? 0));
  const off = c - (val / 100) * c;
  const color = val >= 80 ? "#10b981" : val >= 40 ? "#f59e0b" : "#ef4444";
  return (
    <svg width={size} height={size} className="shrink-0">
      <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.08)" strokeWidth={stroke} fill="none" />
      <circle
        cx={size / 2} cy={size / 2} r={r}
        stroke={color} strokeWidth={stroke} fill="none"
        strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: "stroke-dashoffset 0.6s ease" }}
      />
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" fontSize={size * 0.24} fill={color} fontWeight="bold">
        {pct == null ? "—" : `${val}%`}
      </text>
    </svg>
  );
}

function TodayTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/athlete/today");
      setData(data);
    } catch (e) { /* empty state */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="label-tiny text-text-secondary">LOADING...</div>;

  const session = data?.session;
  const comp = data?.compliance || {};
  const readiness = data?.readiness;
  const rStyle = FLAG_STYLES[readiness?.flag] || FLAG_STYLES.red;

  return (
    <div className="space-y-6" data-testid="today-tab">
      {/* Meet countdown — top of Today so peaking is always visible */}
      <MeetCountdownCard meet={data?.next_meet} onGoProfile={null} />

      {/* Today's session card */}
      <div className="tactical-card p-6" data-testid="today-session-card">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="label-tiny text-brand-accent mb-1">// TODAY&apos;S SESSION</div>
            {session ? (
              <>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-heading text-2xl">{session.session_name}</div>
                  {session.status === "in_progress" ? (
                    <span className="label-tiny text-[10px] text-amber-400 border border-amber-400/40 px-1.5 py-0.5" data-testid="today-session-status">IN PROGRESS</span>
                  ) : (
                    <span className="label-tiny text-[10px] text-brand-accent border border-brand-accent/40 px-1.5 py-0.5" data-testid="today-session-status">SCHEDULED</span>
                  )}
                </div>
                <div className="text-xs text-text-secondary mt-1">
                  {session.program_name} · WEEK {session.week} · DAY {session.day}
                  {session.session_type ? ` · ${String(session.session_type).toUpperCase()}` : ""}
                  {session.coach_name ? ` · COACH ${session.coach_name}` : ""}
                </div>
              </>
            ) : (
              <div className="font-heading text-2xl">NO SESSION QUEUED</div>
            )}
          </div>
          {readiness && (
            <div className={`px-3 py-2 border ${rStyle.border} ${rStyle.bg} ${rStyle.text}`}>
              <div className="label-tiny">READINESS · {(readiness.date || "").slice(0, 10)}</div>
              <div className="font-heading text-xl tabular-nums">{readiness.score} · {rStyle.label}</div>
            </div>
          )}
        </div>

        {session ? (
          <>
            <div className="border-t border-white/5 pt-4">
              <div className="label-tiny text-text-secondary mb-2">
                {session.exercise_count} EXERCISE{session.exercise_count === 1 ? "" : "S"}
              </div>
              <div className="space-y-1 max-h-56 overflow-y-auto no-scrollbar">
                {(session.exercises || []).map((ex, i) => (
                  <div key={i} className="flex items-center justify-between text-sm border border-white/5 px-3 py-2" data-testid={`today-ex-${i}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-heading text-sm truncate">{ex.name}</div>
                      {ex.section && (
                        <div className="label-tiny text-text-secondary uppercase">{ex.section}</div>
                      )}
                    </div>
                    <div className="label-tiny text-brand-accent tabular-nums whitespace-nowrap ml-3">
                      {ex.sets || "—"}×{ex.reps || "—"}
                      {ex.weight_kg ? ` @ ${ex.weight_kg}kg` : (ex.percentage_1rm ? ` @ ${ex.percentage_1rm}%` : "")}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link
                to={`/session/${session.assignment_id}/w/${session.week}/d/${session.day}`}
                className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest inline-flex items-center gap-1.5"
                data-testid="today-start-session-btn"
              >
                <Play size={12} weight="bold" /> {session.status === "in_progress" ? "RESUME SESSION" : "START SESSION"}
              </Link>
              <Link
                to="/programs"
                className="border border-white/10 hover:border-brand-accent hover:text-brand-accent px-4 py-2 text-xs label-tiny tracking-widest inline-flex items-center gap-1.5 transition-colors"
              >
                <ListChecks size={12} weight="bold" /> ALL PROGRAMS
              </Link>
            </div>
          </>
        ) : (
          <div className="text-center py-8" data-testid="today-empty-session">
            <Play size={32} weight="duotone" className="text-brand-accent mx-auto mb-2" />
            <p className="text-sm text-text-secondary">
              Your coach hasn&apos;t queued a session yet. <Link to="/programs" className="text-brand-accent underline">Browse programs</Link> or ping your coach.
            </p>
          </div>
        )}
      </div>

      {/* Compliance + Recent workouts */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="tactical-card p-6 flex items-center gap-6" data-testid="weekly-compliance-card">
          <ComplianceRing pct={comp.pct} size={110} />
          <div>
            <div className="label-tiny text-brand-accent mb-1">// WEEKLY COMPLIANCE</div>
            <div className="font-heading text-3xl tabular-nums" data-testid="compliance-fraction">
              {comp.completed ?? 0}<span className="text-text-secondary text-lg">/{comp.planned ?? 0}</span>
            </div>
            <div className="label-tiny text-text-secondary mt-1">
              WEEK OF {(comp.week_start || "").slice(5)} – {(comp.week_end || "").slice(5)}
            </div>
          </div>
        </div>

        <div className="tactical-card p-6" data-testid="recent-workouts-card">
          <div className="label-tiny text-brand-accent mb-2">// RECENT WORKOUTS</div>
          {(data?.recent_workouts || []).length === 0 ? (
            <div className="text-xs text-text-secondary">No workouts logged yet.</div>
          ) : (
            <div className="space-y-1.5">
              {data.recent_workouts.map((w) => (
                <div key={w.log_id} className="flex items-center justify-between text-sm border-b border-white/5 pb-1.5">
                  <div className="truncate min-w-0 flex-1">
                    <span className="font-heading text-sm">{w.name}</span>
                  </div>
                  <div className="label-tiny text-text-secondary tabular-nums whitespace-nowrap ml-2">
                    {(w.date || "").slice(0, 10)} · {w.duration_min || 0}m
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Wellness — weight, sleep, hydration, mood trends
// ─────────────────────────────────────────────────────────────
function WellnessTab() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ weight_kg: "", hydration_ml: "", steps: "", notes: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/athlete/wellness/trends?days=${days}`);
      setData(data);
    } catch (e) { /* empty */ } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const payload = {};
    if (form.weight_kg !== "") payload.weight_kg = parseFloat(form.weight_kg);
    if (form.hydration_ml !== "") payload.hydration_ml = parseInt(form.hydration_ml);
    if (form.steps !== "") payload.steps = parseInt(form.steps);
    if (form.notes) payload.notes = form.notes;
    if (Object.keys(payload).length === 0) {
      toast.error("Add at least one metric");
      return;
    }
    try {
      await api.post("/athlete/wellness/log", payload);
      toast.success("Wellness logged");
      setForm({ weight_kg: "", hydration_ml: "", steps: "", notes: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  if (loading) return <div className="label-tiny text-text-secondary">LOADING...</div>;

  const series = data?.series || [];
  const avg = data?.averages || {};
  const latest = data?.latest || {};

  return (
    <div className="space-y-6" data-testid="wellness-tab">
      {/* Header + window selector */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label-tiny text-brand-accent mb-1">// {days}-DAY WELLNESS</div>
          <div className="font-heading text-2xl">TRACK THE INPUTS. WATCH THE OUTPUT.</div>
        </div>
        <div className="flex gap-1" data-testid="wellness-window-picker">
          {[7, 30, 90].map((n) => (
            <button
              key={n}
              onClick={() => setDays(n)}
              className={`px-3 py-1.5 text-xs label-tiny tracking-wider border transition-colors ${
                days === n ? "border-brand-accent text-brand-accent" : "border-white/10 text-text-secondary hover:border-white/40"
              }`}
              data-testid={`wellness-window-${n}`}
            >
              {n}D
            </button>
          ))}
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile icon={Scales} label="WEIGHT" value={latest.weight_kg ? `${latest.weight_kg}` : "—"} sub={avg.weight_kg ? `avg ${avg.weight_kg} kg` : "log below"} accent="text-brand-primary" testid="wellness-kpi-weight" />
        <KpiTile icon={MoonStars} label="SLEEP" value={latest.sleep_hours ? `${latest.sleep_hours}h` : "—"} sub={avg.sleep_hours ? `avg ${avg.sleep_hours}h` : "no data"} accent="text-blue-500" testid="wellness-kpi-sleep" />
        <KpiTile icon={Drop} label="HYDRATION" value={latest.hydration_ml ? `${latest.hydration_ml}ml` : "—"} sub={avg.hydration_ml ? `avg ${avg.hydration_ml}ml` : "log below"} accent="text-cyan-500" testid="wellness-kpi-hydration" />
        <KpiTile icon={Heartbeat} label="MOOD" value={latest.mood ?? "—"} sub={avg.mood ? `avg ${avg.mood}/10` : "no data"} accent="text-brand-accent" testid="wellness-kpi-mood" />
      </div>

      {/* Charts */}
      {series.length === 0 ? (
        <div className="tactical-card p-10 text-center" data-testid="wellness-empty">
          <Scales size={40} weight="duotone" className="text-brand-accent mx-auto mb-3" />
          <div className="font-heading text-xl mb-1">NO WELLNESS DATA YET</div>
          <p className="text-sm text-text-secondary">Log below or run a daily readiness check-in.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="tactical-card p-5" data-testid="chart-weight">
            <div className="label-tiny text-brand-accent mb-3">// WEIGHT (kg)</div>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={series}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="date" stroke="#A1A5B5" fontSize={10} />
                <YAxis stroke="#A1A5B5" fontSize={10} />
                <Tooltip contentStyle={{ background: "#12141A", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }} />
                <Line type="monotone" dataKey="weight_kg" stroke="#4169E1" strokeWidth={2} dot={{ r: 3, fill: "#FFEA00" }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="tactical-card p-5" data-testid="chart-sleep">
            <div className="label-tiny text-brand-accent mb-3">// SLEEP (hours)</div>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={series}>
                <defs>
                  <linearGradient id="sleepG" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="date" stroke="#A1A5B5" fontSize={10} />
                <YAxis domain={[0, 12]} stroke="#A1A5B5" fontSize={10} />
                <Tooltip contentStyle={{ background: "#12141A", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }} />
                <ReferenceLine y={7} stroke="#10b981" strokeDasharray="4 4" />
                <Area type="monotone" dataKey="sleep_hours" stroke="#3b82f6" fill="url(#sleepG)" strokeWidth={2} connectNulls />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="tactical-card p-5" data-testid="chart-hydration">
            <div className="label-tiny text-brand-accent mb-3">// HYDRATION (ml)</div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={series}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="date" stroke="#A1A5B5" fontSize={10} />
                <YAxis stroke="#A1A5B5" fontSize={10} />
                <Tooltip contentStyle={{ background: "#12141A", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }} cursor={{ fill: "rgba(6,182,212,0.06)" }} />
                <Bar dataKey="hydration_ml" fill="#06b6d4" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="tactical-card p-5" data-testid="chart-mood">
            <div className="label-tiny text-brand-accent mb-3">// MOOD (1-10)</div>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={series}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="date" stroke="#A1A5B5" fontSize={10} />
                <YAxis domain={[0, 10]} stroke="#A1A5B5" fontSize={10} />
                <Tooltip contentStyle={{ background: "#12141A", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }} />
                <Line type="monotone" dataKey="mood" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3, fill: "#f59e0b" }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Log form */}
      <div className="tactical-card p-6" data-testid="wellness-log-form">
        <div className="label-tiny text-brand-accent mb-3">// LOG WELLNESS · TODAY</div>
        <div className="grid md:grid-cols-4 gap-3">
          <input
            type="number" step="0.1"
            placeholder="Weight (kg)"
            value={form.weight_kg}
            onChange={(e) => setForm({ ...form, weight_kg: e.target.value })}
            className="bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
            data-testid="wellness-input-weight"
          />
          <input
            type="number"
            placeholder="Hydration (ml)"
            value={form.hydration_ml}
            onChange={(e) => setForm({ ...form, hydration_ml: e.target.value })}
            className="bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
            data-testid="wellness-input-hydration"
          />
          <input
            type="number"
            placeholder="Steps"
            value={form.steps}
            onChange={(e) => setForm({ ...form, steps: e.target.value })}
            className="bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
            data-testid="wellness-input-steps"
          />
          <input
            type="text"
            placeholder="Notes"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
            data-testid="wellness-input-notes"
          />
        </div>
        <div className="mt-4 flex justify-end">
          <button onClick={submit} className="btn-kinetic px-5 py-2 text-xs label-tiny tracking-widest inline-flex items-center gap-1.5" data-testid="wellness-submit">
            <CheckCircle size={12} weight="bold" /> SAVE LOG
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Profile — bio, goals, meets, coach
// ─────────────────────────────────────────────────────────────
function FamilyOnboardingBanner() {
  const { user, setUser } = useAuth();
  const [dismissing, setDismissing] = useState(false);
  const dismissed = !!user?.onboarding_dismissed?.family_guardians;

  const dismiss = async () => {
    setDismissing(true);
    try {
      await api.post("/onboarding/hint/dismiss", { key: "family_guardians" });
      setUser({
        ...user,
        onboarding_dismissed: { ...(user.onboarding_dismissed || {}), family_guardians: true },
      });
    } catch { /* silent — banner will re-appear next load */ }
    finally { setDismissing(false); }
  };

  const scrollToCard = () => {
    const el = document.querySelector('[data-testid="profile-linked-family"]');
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  if (dismissed) return null;

  return (
    <div
      className="border border-amber-400/60 bg-amber-500/10 p-4 flex items-start gap-3 relative overflow-hidden"
      data-testid="family-onboarding-banner"
    >
      <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-amber-400/20 blur-2xl pointer-events-none" />
      <div className="text-2xl leading-none shrink-0" aria-hidden="true">👋</div>
      <div className="flex-1 min-w-0 relative z-10">
        <div className="label-tiny text-amber-300 mb-1">// NEW · YOU&apos;RE IN CONTROL</div>
        <div className="font-heading text-lg text-amber-100">Meet your Family &amp; Guardians panel</div>
        <div className="text-xs text-amber-100/80 mt-1 leading-relaxed">
          Scroll down to see every parent or guardian currently mirroring your training data.
          Revoke access any time — one click, no drama.
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={scrollToCard}
            className="border border-amber-300 text-amber-200 hover:bg-amber-400 hover:text-slate-900 text-[10px] label-tiny tracking-widest px-3 py-1.5 transition-colors"
            data-testid="family-onboarding-show"
          >
            SHOW ME →
          </button>
          <button
            onClick={dismiss}
            disabled={dismissing}
            className="text-[10px] label-tiny tracking-widest text-amber-200/70 hover:text-amber-100 px-2 py-1.5 disabled:opacity-50"
            data-testid="family-onboarding-dismiss"
          >
            {dismissing ? "SAVING…" : "GOT IT"}
          </button>
        </div>
      </div>
    </div>
  );
}

function LinkedFamilyCard() {
  const [rows, setRows] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState({});
  const [responding, setResponding] = useState({});

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/athlete/hub/parents").then(({ data }) => setRows(data.items || [])).catch(() => setRows([])),
      api.get("/athlete/hub/parent-requests").then(({ data }) => setPending(data.items || [])).catch(() => setPending([])),
    ]).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const respond = async (link, action) => {
    setResponding((s) => ({ ...s, [link.link_id]: true }));
    try {
      await api.post(`/athlete/hub/parent-requests/${link.link_id}/respond`, { action });
      toast.success(action === "accept"
        ? `${link.parent_name || "Guardian"} can now follow your training`
        : "Request declined");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not respond");
    } finally {
      setResponding((s) => ({ ...s, [link.link_id]: false }));
    }
  };

  const revoke = async (p) => {
    const label = p.name || p.email || "this account";
    if (!window.confirm(`Revoke ${label}'s access to your training data?\n\nThey will lose access to your attendance, tests, competitions and payments. You can re-approve any time by having them link you again.`)) return;
    setRevoking((s) => ({ ...s, [p.user_id]: true }));
    try {
      await api.post(`/athlete/hub/parents/${p.user_id}/revoke`);
      toast.success(`${label} unlinked`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revoke");
    } finally {
      setRevoking((s) => ({ ...s, [p.user_id]: false }));
    }
  };

  const setRelationship = async (p, relationship) => {
    try {
      await api.post(`/athlete/hub/parents/${p.user_id}/relationship`, { relationship });
      setRows((rs) => rs.map((r) => (r.user_id === p.user_id ? { ...r, relationship } : r)));
      toast.success("Relationship updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  if (loading) return null;

  return (
    <div className="tactical-card p-6" data-testid="profile-linked-family">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="label-tiny text-brand-accent mb-1">// FAMILY & GUARDIANS</div>
          <div className="font-heading text-xl">Linked to your profile</div>
          <div className="text-xs text-text-secondary mt-1">
            {rows.length === 0
              ? "No family accounts are mirroring your data right now."
              : `${rows.length} parent${rows.length !== 1 ? "s / guardians" : " / guardian"} can see your attendance, tests, competitions and payments — read-only.`}
          </div>
        </div>
      </div>

      {pending.length > 0 && (
        <div className="mb-4 space-y-2" data-testid="profile-guardian-requests">
          <div className="label-tiny text-amber-400">// AWAITING YOUR CONFIRMATION</div>
          {pending.map((r) => (
            <div key={r.link_id} className="border border-amber-400/50 bg-amber-500/10 p-3" data-testid={`guardian-request-${r.link_id}`}>
              <div className="text-sm">
                <span className="font-heading">{r.parent_name || "A parent"}</span>
                <span className="text-text-secondary"> wants to follow your training as your {r.relationship || "parent"}.</span>
              </div>
              <div className="text-[11px] text-text-secondary mt-1">{r.parent_email}</div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => respond(r, "accept")}
                  disabled={responding[r.link_id]}
                  className="text-[10px] label-tiny tracking-widest btn-kinetic px-4 py-1.5 disabled:opacity-50"
                  data-testid={`guardian-accept-${r.link_id}`}
                >
                  {responding[r.link_id] ? "…" : "ACCEPT"}
                </button>
                <button
                  onClick={() => respond(r, "decline")}
                  disabled={responding[r.link_id]}
                  className="text-[10px] label-tiny tracking-widest border border-white/20 text-text-secondary hover:border-red-500/60 hover:text-red-400 px-4 py-1.5 disabled:opacity-50 transition-colors"
                  data-testid={`guardian-decline-${r.link_id}`}
                >
                  DECLINE
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {rows.length === 0 ? (
        <div className="border border-dashed border-slate-700 p-4 text-center text-xs text-text-secondary" data-testid="profile-linked-family-empty">
          A parent or guardian will appear here after you confirm their request.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((p) => (
            <div key={p.user_id} className="flex items-center gap-3 border border-slate-700 bg-slate-900/40 p-3" data-testid={`profile-parent-${p.user_id}`}>
              <div className="w-10 h-10 bg-amber-500/20 border border-amber-400/50 flex items-center justify-center text-sm font-heading text-amber-300 shrink-0">
                {(p.name || "P").slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading text-sm truncate">{p.name || "Parent"}</div>
                <div className="text-[10px] tracking-widest text-text-secondary truncate">{p.email}</div>
              </div>
              <select
                value={p.relationship || "parent"}
                onChange={(e) => setRelationship(p, e.target.value)}
                className="bg-slate-800 border border-slate-500 text-[11px] text-white px-2 py-1.5 focus:border-brand-accent outline-none shrink-0"
                data-testid={`profile-relationship-${p.user_id}`}
                title="How is this guardian related to you?"
              >
                <option value="parent" className="bg-slate-900 text-white">Parent</option>
                <option value="guardian" className="bg-slate-900 text-white">Guardian</option>
                <option value="coach_contact" className="bg-slate-900 text-white">Coach's contact</option>
              </select>
              <button
                onClick={() => revoke(p)}
                disabled={revoking[p.user_id]}
                className="text-[10px] label-tiny tracking-widest border border-red-500/60 text-red-400 hover:bg-red-500 hover:text-white px-3 py-1.5 disabled:opacity-50 transition-colors"
                data-testid={`profile-revoke-${p.user_id}`}
              >
                {revoking[p.user_id] ? "REVOKING…" : "REVOKE"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProfileTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ bio: "", height_cm: "", weight_kg: "", goals_text: "", training_focus: "", sport: "" });
  const [meetForm, setMeetForm] = useState({ name: "", discipline: "", date: "", location: "", goal: "", notes: "" });
  const [meetOpen, setMeetOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/athlete/profile");
      setData(data);
      setForm({
        bio: data.user?.bio || "",
        height_cm: data.user?.height_cm || "",
        weight_kg: data.user?.weight_kg || "",
        goals_text: data.user?.goals_text || "",
        training_focus: data.user?.training_focus || "",
        sport: data.user?.sport || "",
      });
    } catch (e) { /* empty */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      const payload = {
        bio: form.bio || null,
        height_cm: form.height_cm === "" ? null : parseFloat(form.height_cm),
        weight_kg: form.weight_kg === "" ? null : parseFloat(form.weight_kg),
        goals_text: form.goals_text || null,
        training_focus: form.training_focus || null,
        sport: form.sport || null,
      };
      await api.patch("/athlete/profile", payload);
      toast.success("Profile saved");
      setEditing(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const submitMeet = async () => {
    if (!meetForm.name.trim() || !meetForm.date) {
      toast.error("Name & date required");
      return;
    }
    try {
      await api.post("/athlete/meets", meetForm);
      toast.success("Meet added");
      setMeetForm({ name: "", discipline: "", date: "", location: "", goal: "", notes: "" });
      setMeetOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const deleteMeet = async (id) => {
    try {
      await api.delete(`/athlete/meets/${id}`);
      toast.success("Meet removed");
      await load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  if (loading) return <div className="label-tiny text-text-secondary">LOADING...</div>;

  const u = data?.user || {};
  const goals = data?.goals || [];
  const meets = data?.meets || [];
  const coaches = data?.coaches || [];
  const today = new Date().toISOString().slice(0, 10);
  const upcomingMeets = meets.filter((m) => (m.date || "") >= today);
  const pastMeets = meets.filter((m) => (m.date || "") < today);

  return (
    <div className="space-y-6" data-testid="profile-tab">
      {/* Identity card */}
      <div className="tactical-card p-6" data-testid="profile-identity-card">
        <div className="flex items-start gap-5 flex-wrap">
          {u.picture ? (
            <img src={u.picture} alt="" className="w-24 h-24 rounded-full object-cover border-2 border-brand-accent" />
          ) : (
            <div className="w-24 h-24 rounded-full bg-brand-primary flex items-center justify-center text-3xl font-bold border-2 border-brand-accent">
              {u.name?.[0]?.toUpperCase() || "?"}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="label-tiny text-brand-accent mb-1">// ATHLETE PROFILE</div>
            <div className="font-heading text-3xl">{u.name || "—"}</div>
            <div className="text-xs text-text-secondary">{u.email}</div>
            {!editing && u.bio && <div className="text-sm mt-2">{u.bio}</div>}
            {!editing && (
              <div className="text-xs text-text-secondary mt-2 flex gap-3 flex-wrap">
                {u.sport && <span>SPORT: <span className="text-white">{u.sport}</span></span>}
                {u.training_focus && <span>FOCUS: <span className="text-white">{u.training_focus}</span></span>}
                {u.height_cm && <span>HT: <span className="text-white">{u.height_cm} cm</span></span>}
                {u.weight_kg && <span>WT: <span className="text-white">{u.weight_kg} kg</span></span>}
              </div>
            )}
          </div>
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="border border-brand-accent text-brand-accent hover:bg-brand-accent hover:text-surface-base px-3 py-1.5 text-xs label-tiny tracking-widest transition-colors inline-flex items-center gap-1"
              data-testid="profile-edit-btn"
            >
              <PencilSimple size={12} weight="bold" /> EDIT
            </button>
          )}
        </div>

        {editing && (
          <div className="mt-4 grid md:grid-cols-2 gap-3 border-t border-white/5 pt-4">
            <input
              placeholder="Sport (e.g. Powerlifting)"
              value={form.sport}
              onChange={(e) => setForm({ ...form, sport: e.target.value })}
              className="bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
              data-testid="profile-input-sport"
            />
            <input
              placeholder="Training Focus (e.g. Strength peak)"
              value={form.training_focus}
              onChange={(e) => setForm({ ...form, training_focus: e.target.value })}
              className="bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
              data-testid="profile-input-focus"
            />
            <input
              type="number" step="0.1"
              placeholder="Height (cm)"
              value={form.height_cm}
              onChange={(e) => setForm({ ...form, height_cm: e.target.value })}
              className="bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
              data-testid="profile-input-height"
            />
            <input
              type="number" step="0.1"
              placeholder="Weight (kg)"
              value={form.weight_kg}
              onChange={(e) => setForm({ ...form, weight_kg: e.target.value })}
              className="bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
              data-testid="profile-input-weight"
            />
            <textarea
              placeholder="Bio — one line about you as an athlete"
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              rows={2}
              className="md:col-span-2 bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
              data-testid="profile-input-bio"
            />
            <textarea
              placeholder="Goals — what you're chasing this cycle"
              value={form.goals_text}
              onChange={(e) => setForm({ ...form, goals_text: e.target.value })}
              rows={2}
              className="md:col-span-2 bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
              data-testid="profile-input-goals-text"
            />
            <div className="md:col-span-2 flex justify-end gap-2">
              <button onClick={() => setEditing(false)} className="border border-white/10 px-4 py-2 text-xs label-tiny tracking-widest hover:border-white/40 transition-colors" data-testid="profile-cancel">
                CANCEL
              </button>
              <button onClick={submit} className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest" data-testid="profile-save">
                SAVE
              </button>
            </div>
          </div>
        )}
      </div>

      {/* One-time onboarding banner for the Family panel */}
      <FamilyOnboardingBanner />

      {/* Family / Guardians — athletes see who's mirroring their data + can revoke */}
      <LinkedFamilyCard />

      {/* Goals + Coach + Meets grid */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Goals */}
        <div className="tactical-card p-6" data-testid="profile-goals-card">
          <div className="label-tiny text-brand-accent mb-3">// GOALS</div>
          {u.goals_text && (
            <div className="text-sm mb-3 pb-3 border-b border-white/5 whitespace-pre-line">{u.goals_text}</div>
          )}
          {goals.length === 0 && !u.goals_text ? (
            <div className="text-xs text-text-secondary">
              No active goals. <Link to="/goals" className="text-brand-accent underline">Add one</Link>.
            </div>
          ) : (
            <div className="space-y-2">
              {goals.map((g) => (
                <div key={g.goal_id} className="flex items-center justify-between border border-white/5 px-3 py-2" data-testid={`goal-${g.goal_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-heading text-sm truncate">{g.title}</div>
                    {g.deadline && <div className="label-tiny text-text-secondary">by {(g.deadline || "").slice(0, 10)}</div>}
                  </div>
                  <div className="text-xs tabular-nums label-tiny text-brand-accent whitespace-nowrap ml-2">
                    {g.current_value || 0} / {g.target_value || 0} {g.unit}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Coach info */}
        <div className="tactical-card p-6" data-testid="profile-coach-card">
          <div className="label-tiny text-brand-accent mb-3">// YOUR COACH{coaches.length === 1 ? "" : "ES"}</div>
          {coaches.length === 0 ? (
            <div className="text-xs text-text-secondary">
              Not linked to a coach. <Link to="/discover" className="text-brand-accent underline">Find one</Link>.
            </div>
          ) : (
            <div className="space-y-3">
              {coaches.map((c) => (
                <div key={c.coach_id} className="flex items-center gap-3" data-testid={`coach-${c.coach_id}`}>
                  {c.picture ? (
                    <img src={c.picture} alt="" className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-brand-primary flex items-center justify-center text-lg font-bold">
                      {c.name?.[0]?.toUpperCase() || "?"}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-heading text-sm truncate">{c.name}</div>
                    <div className="text-xs text-text-secondary">{c.coach_specialty || "Coach"}</div>
                    {c.since && <div className="label-tiny text-text-secondary">since {(c.since || "").slice(0, 10)}</div>}
                  </div>
                  {c.coach_slug && (
                    <Link to={`/c/${c.coach_slug}`} className="border border-white/10 hover:border-brand-accent hover:text-brand-accent px-2 py-1 text-[10px] label-tiny tracking-wider transition-colors">
                      VIEW
                    </Link>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Meets */}
      <div className="tactical-card p-6" data-testid="profile-meets-card">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div>
            <div className="label-tiny text-brand-accent mb-1">// MEETS &amp; EVENTS</div>
            <div className="font-heading text-xl">
              {upcomingMeets.length} UPCOMING · {pastMeets.length} PAST
            </div>
          </div>
          <button
            onClick={() => setMeetOpen(!meetOpen)}
            className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest inline-flex items-center gap-1.5"
            data-testid="meet-add-btn"
          >
            <Plus size={12} weight="bold" /> ADD MEET
          </button>
        </div>

        {meetOpen && (
          <div className="border border-white/5 p-4 mb-4" data-testid="meet-form">
            <div className="grid md:grid-cols-3 gap-3">
              <input
                placeholder="Meet name*"
                value={meetForm.name}
                onChange={(e) => setMeetForm({ ...meetForm, name: e.target.value })}
                className="bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
                data-testid="meet-input-name"
              />
              <input
                type="date"
                value={meetForm.date}
                onChange={(e) => setMeetForm({ ...meetForm, date: e.target.value })}
                className="bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
                data-testid="meet-input-date"
              />
              <input
                placeholder="Discipline (e.g. Powerlifting)"
                value={meetForm.discipline}
                onChange={(e) => setMeetForm({ ...meetForm, discipline: e.target.value })}
                className="bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
                data-testid="meet-input-discipline"
              />
              <input
                placeholder="Location"
                value={meetForm.location}
                onChange={(e) => setMeetForm({ ...meetForm, location: e.target.value })}
                className="bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
                data-testid="meet-input-location"
              />
              <input
                placeholder="Goal (e.g. Total 500kg)"
                value={meetForm.goal}
                onChange={(e) => setMeetForm({ ...meetForm, goal: e.target.value })}
                className="bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
                data-testid="meet-input-goal"
              />
              <input
                placeholder="Notes"
                value={meetForm.notes}
                onChange={(e) => setMeetForm({ ...meetForm, notes: e.target.value })}
                className="bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
                data-testid="meet-input-notes"
              />
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setMeetOpen(false)} className="border border-white/10 px-3 py-1.5 text-xs label-tiny tracking-widest hover:border-white/40 transition-colors">
                CANCEL
              </button>
              <button onClick={submitMeet} className="btn-kinetic px-3 py-1.5 text-xs label-tiny tracking-widest" data-testid="meet-submit">
                SAVE
              </button>
            </div>
          </div>
        )}

        {meets.length === 0 ? (
          <div className="text-xs text-text-secondary text-center py-4" data-testid="meets-empty">
            No meets yet. Add your next comp or key training checkpoint.
          </div>
        ) : (
          <div className="space-y-2">
            {[...upcomingMeets, ...pastMeets].map((m) => {
              const isPast = (m.date || "") < today;
              return (
                <div key={m.meet_id} className={`flex items-center gap-3 border px-3 py-2 ${isPast ? "border-white/5 opacity-60" : "border-brand-accent/30 bg-brand-accent/5"}`} data-testid={`meet-${m.meet_id}`}>
                  <Flag size={16} weight="duotone" className={isPast ? "text-text-secondary" : "text-brand-accent"} />
                  <div className="flex-1 min-w-0">
                    <div className="font-heading text-sm truncate">{m.name}</div>
                    <div className="text-xs text-text-secondary flex gap-2 flex-wrap">
                      <span>{m.date}</span>
                      {m.discipline && <span>· {m.discipline}</span>}
                      {m.location && <span className="inline-flex items-center gap-0.5"><MapPin size={10} /> {m.location}</span>}
                      {m.goal && <span>· GOAL: {m.goal}</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => deleteMeet(m.meet_id)}
                    className="text-text-secondary hover:text-red-500 transition-colors"
                    data-testid={`meet-delete-${m.meet_id}`}
                  >
                    <Trash size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Root
// ─────────────────────────────────────────────────────────────
export default function AthleteHub() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab") || "overview";
  const tab = TABS.some((t) => t.id === tabParam) ? tabParam : "overview";
  const setTab = (t) => setSearchParams({ tab: t }, { replace: false });
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadOverview = useCallback(async () => {
    try {
      const { data } = await api.get("/athlete/hub/overview");
      setOverview(data);
    } catch (e) { /* handled by empty state */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="athlete-hub-page">
      <ConsentPendingBanner />
      <DailyCheckIn />
      <GuidedTour
        enabled={tab === "overview" && !loading && !user?.onboarding_dismissed?.athlete_tour}
        steps={ATHLETE_TOUR_STEPS}
        dismissKey="athlete_tour"
      />
      <div className="mb-6">
        <div className="label-tiny text-brand-accent mb-2 flex items-center gap-2">
          <span className="energy-dot"></span> // ATHLETE COMMAND DECK
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl">
          READY UP, <span className="text-heat">{user?.name?.split(" ")[0]?.toUpperCase()}.</span>
        </h1>
        <p className="text-text-secondary text-sm mt-2">
          Readiness · Coach comms · PR chase — all in one deck.
        </p>
      </div>

      <div className="border-b border-white/10 flex gap-1 overflow-x-auto mb-6" data-testid="athlete-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`athlete-tab-${t.id}`}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm label-tiny tracking-wider transition border-b-2 -mb-px ${
              tab === t.id
                ? "border-heat text-heat"
                : "border-transparent text-text-secondary hover:text-heat"
            }`}
          >
            <t.icon size={14} weight="duotone" /> {t.label.toUpperCase()}
            {t.id === "coach" && overview?.unread_messages > 0 && (
              <span className="bg-red-500 text-white text-[10px] rounded-full px-1.5 py-0.5">{overview.unread_messages}</span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="label-tiny text-text-secondary">LOADING...</div>
      ) : (
        <>
          {tab === "overview"  && <><AthleteQuickStart onGoTab={setTab} /><OverviewTab overview={overview} onGoTab={setTab} /></>}
          {tab === "today"     && <TodayTab />}
          {tab === "readiness" && <ReadinessTab onCheckedIn={loadOverview} />}
          {tab === "coach"     && <CoachChatTab overview={overview} onNudge={loadOverview} />}
          {tab === "prs"       && <PRBoardTab />}
          {tab === "wellness"  && <WellnessTab />}
          {tab === "profile"   && <ProfileTab />}
        </>
      )}
    </div>
  );
}
